// Phase T2.0A Prompt 2 — Admin event-metadata + flyer editor.
//
// Used inside TournamentManagePage and RoundRobinManagePage. Now exposes
// every pre-start editable field the admin needs (name, capacity,
// composition, plus RR-only pool/final controls) — not just the small
// promotional subset. Data-integrity validation lives on the backend
// (capacity_below_registered, composition_incompatible_with_registrations,
// event_settings_locked_after_start).

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import EventFlyerUpload from './EventFlyerUpload';
import EventPhotoManager from './EventPhotoManager';
import EventDetailsFieldset from './EventDetailsFieldset';
import { isEventStarted } from '../../lib/eventLifecycle';

// Turn an axios error from PATCH /tournaments|/round-robins/<id> into a
// human-readable toast. Handles the three shapes we actually see:
//   1) FastAPI HTTPException(400, {code, message, …})  → detail = {code,message,…}
//   2) FastAPI HTTPException(400, "some string")       → detail = "…"
//   3) Pydantic 422                                     → detail = [{loc, msg, …}]
function extractSaveErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation — surface the first field-level message.
    const first = detail[0];
    if (first?.msg) {
      const field = Array.isArray(first.loc) ? first.loc.filter(p => p !== 'body').join('.') : null;
      return field ? `${field}: ${first.msg}` : first.msg;
    }
  }
  if (detail && typeof detail === 'object') {
    // Include a hint for `composition_incompatible_with_registrations` count.
    if (detail.code === 'composition_incompatible_with_registrations' && detail.count) {
      return detail.message || `Cannot change composition — ${detail.count} registered entries would be invalid.`;
    }
    if (detail.message) return detail.message;
    if (detail.code) return detail.code;
  }
  if (err?.message) return err.message;
  return 'Save failed.';
}

function extractDetails(event) {
  return {
    name: event.name || '',
    description: event.description || '',
    location_text: event.location_text || '',
    starts_at: event.starts_at || null,
    ends_at: event.ends_at || null,
    entry_fee_cents: event.entry_fee_cents ?? null,
    payment_type: event.payment_type || null,
    payment_link: event.payment_link || null,
    match_format_text: event.match_format_text || '',
    max_entries: event.max_entries ?? null,
    composition: event.composition || 'open',
    // RR-only structural.
    num_pools: event.num_pools ?? null,
    has_final: event.has_final ?? null,
    // Home Featured.
    featured_on_home: !!event.featured_on_home,
    featured_order: event.featured_order ?? '',
  };
}

export default function EventEditor({
  event,             // hydrated event object
  eventKind,         // 'tournament' | 'round_robin'
  flyerUrl,          // event.flyer_url
  onSaved,           // () => void (called after any successful update)
  api,               // { updateEvent(payload), uploadFlyer(file), deleteFlyer() }
  isAdmin = false,   // global admin retains full control over ARCHIVED events
}) {
  const [details, setDetails] = useState(() => extractDetails(event));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDetails(extractDetails(event));
    setDirty(false);
  }, [event.updated_at]);

  const isRR = eventKind === 'round_robin';
  const eventType = isRR ? event.rr_type : event.tournament_type;
  const isDoubles = eventType === 'doubles';
  const capacityUnit = isDoubles ? 'teams' : 'players';
  // GLOBAL ADMIN is never lifecycle-locked — admins edit freely in every
  // status. Non-admin managers stay locked once the event has started
  // (backend mirrors this in event_lifecycle.is_edit_locked / raise_if_locked_field_change).
  const locked = isEventStarted(event.status) && !isAdmin;
  // Bracket/schedule generated ⇒ metadata still editable, but structural
  // fields that would invalidate generated competition data are held
  // behind the existing reset flow. Backend also enforces this.
  const scheduleGenerated = event.status === 'schedule_generated';
  const bracketGenerated  = event.status === 'bracket_generated';

  const patch = (v) => { setDetails(v); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      // When the event is locked (in_progress / completed), the ONLY fields
      // the backend accepts are the Home Featured merchandising controls.
      // Sending the full metadata payload would trip the post-start lock, so
      // we send just the featured fields — this keeps the featured toggle
      // working without touching locked competitive config. Admins editing an
      // archived event are NOT locked and fall through to the full payload.
      if (locked) {
        await api.updateEvent({
          featured_on_home: !!details.featured_on_home,
          featured_order: details.featured_on_home
            ? (details.featured_order === '' || details.featured_order == null
                ? null
                : parseInt(details.featured_order, 10))
            : null,
        });
        toast.success('Event details saved.');
        setDirty(false);
        onSaved?.();
        return;
      }
      const payload = {
        name: details.name || null,
        description: details.description || null,
        location_text: details.location_text || null,
        starts_at: details.starts_at || null,
        ends_at: details.ends_at || null,
        entry_fee_cents: details.entry_fee_cents,
        payment_type: details.payment_type || null,
        // Stripe uses Embedded Checkout on the event page — no external
        // link is needed or accepted.
        payment_link: details.payment_type === 'stripe'
          ? null
          : (details.payment_link || null),
        max_entries: details.max_entries === '' ? null : details.max_entries,
        composition: details.composition || 'open',
        featured_on_home: !!details.featured_on_home,
        featured_order: details.featured_on_home
          ? (details.featured_order === '' || details.featured_order == null
              ? null
              : parseInt(details.featured_order, 10))
          : null,
      };
      if (!event.scoring_format) payload.match_format_text = details.match_format_text || null;
      if (isRR) {
        payload.num_pools = details.num_pools == null || details.num_pools === '' ? null : parseInt(details.num_pools, 10);
        payload.has_final = !!details.has_final;
      }
      await api.updateEvent(payload);
      toast.success('Event details saved.');
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(extractSaveErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadFlyer = async (file) => {
    try { await api.uploadFlyer(file); onSaved?.(); }
    catch (e) { toast.error(extractSaveErrorMessage(e)); throw e; }
  };
  const removeFlyer = async () => {
    try { await api.deleteFlyer(); onSaved?.(); }
    catch (e) { toast.error(extractSaveErrorMessage(e)); throw e; }
  };

  // Composition options: `mixed` is doubles-only.
  const compositionOptions = [
    { value: 'open',   label: 'Open (any gender)' },
    { value: 'mens',   label: "Men's" },
    { value: 'womens', label: "Women's" },
    ...(isDoubles ? [{ value: 'mixed', label: 'Mixed doubles' }] : []),
  ];

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5 space-y-5" data-testid="event-editor">
      {locked && (
        <div
          className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2 text-[12.5px]"
          data-testid="event-editor-locked-banner"
          role="status"
        >
          This event has already started. Event settings are locked.
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Title</span>
          <input
            type="text"
            data-testid="event-editor-name"
            disabled={locked}
            value={details.name}
            onChange={(e) => patch({ ...details, name: e.target.value })}
            className="mt-1 w-full border border-[#e3dcd1] rounded-lg px-3 py-2 text-sm min-h-[44px] disabled:bg-[#f6f4ef] disabled:text-[#9aa39d]"
            placeholder="e.g. Rippers Mixed Doubles"
          />
        </label>
      </div>

      {/* Flyer */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold mb-2">Flyer / Poster</div>
        <EventFlyerUpload
          flyerUrl={flyerUrl}
          onUpload={uploadFlyer}
          onRemove={removeFlyer}
          eventName={event.name}
          disabled={locked}
        />
      </div>

      {/* Additional photos gallery */}
      <div>
        <EventPhotoManager kind={eventKind} eventId={event.id} />
      </div>

      {/* Date, Location, Fee, Payments — existing shared fieldset */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold mb-2">Date, Location &amp; Description</div>
        <fieldset disabled={locked} className="disabled:opacity-70">
          <EventDetailsFieldset value={details} onChange={patch} structuredFormat={!!event.scoring_format} />
        </fieldset>
      </div>

      {/* Capacity + Composition (+ RR structural) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Capacity</span>
          <input
            type="number"
            min={1}
            max={isRR ? 64 : 128}
            step={1}
            data-testid="event-editor-max-entries"
            disabled={locked}
            value={details.max_entries ?? ''}
            onChange={(e) => patch({ ...details, max_entries: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
            className="mt-1 w-full border border-[#e3dcd1] rounded-lg px-3 py-2 text-sm min-h-[44px] disabled:bg-[#f6f4ef] disabled:text-[#9aa39d]"
            placeholder="e.g. 16"
          />
          <div className="text-[11px] text-[#6a766e] mt-1" data-testid="event-editor-max-hint">
            {isDoubles
              ? `Doubles: number of teams. ${details.max_entries ? `${details.max_entries} teams = ${details.max_entries * 2} effective players.` : '(Effective players = teams × 2.)'}`
              : 'Singles: number of players.'}
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Composition</span>
          <select
            data-testid="event-editor-composition"
            disabled={locked}
            value={details.composition || 'open'}
            onChange={(e) => patch({ ...details, composition: e.target.value })}
            className="mt-1 w-full border border-[#e3dcd1] rounded-lg px-3 py-2 text-sm min-h-[44px] bg-white disabled:bg-[#f6f4ef] disabled:text-[#9aa39d]"
          >
            {compositionOptions.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* RR-only structural: pools + finals */}
      {isRR && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="event-editor-rr-structural">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Number of pools</span>
            <input
              type="number"
              min={1}
              max={8}
              step={1}
              data-testid="event-editor-num-pools"
              disabled={locked || scheduleGenerated}
              value={details.num_pools ?? ''}
              onChange={(e) => patch({ ...details, num_pools: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
              className="mt-1 w-full border border-[#e3dcd1] rounded-lg px-3 py-2 text-sm min-h-[44px] disabled:bg-[#f6f4ef] disabled:text-[#9aa39d]"
            />
            {scheduleGenerated && (
              <div className="text-[11px] text-amber-800 mt-1">Reset the schedule to change the pool count.</div>
            )}
          </label>
          <label className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              data-testid="event-editor-has-final"
              disabled={locked || scheduleGenerated}
              checked={!!details.has_final}
              onChange={(e) => patch({ ...details, has_final: e.target.checked })}
              className="w-4 h-4 accent-[#013523]"
            />
            <span className="text-sm text-[#013523]">Include a Final</span>
          </label>
        </div>
      )}

      {/* Home Featured toggle — remains editable even after start. */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold mb-2">Home Page</div>
        <label
          className="flex items-start gap-3 border border-[#e3dcd1] rounded-xl px-3 py-3 cursor-pointer min-h-[52px]"
          data-testid="event-editor-featured-label"
        >
          <input
            type="checkbox"
            checked={!!details.featured_on_home}
            onChange={(e) => patch({ ...details, featured_on_home: e.target.checked })}
            data-testid="event-editor-featured-toggle"
            className="mt-1 w-4 h-4 accent-[#013523]"
          />
          <div className="flex-1">
            <div className="text-[13.5px] text-[#013523] font-medium">Feature on Home Page</div>
            <div className="text-[11.5px] text-[#6a766e] mt-0.5">
              When checked, this event appears in the members&apos; Home featured rotation.
              Admin controls stay in charge — nothing shows there unless you explicitly turn it on.
            </div>
            {details.featured_on_home && (
              <div className="mt-3 flex items-center gap-2" data-testid="event-editor-featured-order-row">
                <label htmlFor="event-editor-featured-order" className="text-[11.5px] text-[#013523]">
                  Order (optional):
                </label>
                <input
                  id="event-editor-featured-order"
                  type="number"
                  min={1}
                  max={999}
                  step={1}
                  value={details.featured_order}
                  onChange={(e) => patch({ ...details, featured_order: e.target.value })}
                  data-testid="event-editor-featured-order"
                  className="w-24 border border-[#e3dcd1] rounded-lg text-sm px-2 py-1.5"
                  placeholder="e.g. 1"
                />
                <span className="text-[10.5px] text-[#9aa39d]">smaller shows first</span>
              </div>
            )}
          </div>
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || busy}
          data-testid="event-editor-save"
          className="px-4 py-2 rounded-full bg-[#013523] text-white text-sm min-h-[44px] disabled:opacity-50"
        >
          {busy ? 'Saving…' : (dirty ? 'Save event details' : 'All changes saved')}
        </button>
      </div>
      {(bracketGenerated || scheduleGenerated) && !locked && (
        <div className="text-[11px] text-[#8a6d1a]" data-testid="event-editor-structural-note">
          {bracketGenerated
            ? 'Bracket generated — roster/seeding changes require Reset Bracket below.'
            : 'Schedule generated — pool/final structural changes require Reset Schedule below.'}
        </div>
      )}
    </div>
  );
}
